if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    static get SELECTORS() {
      return {
        viewer: '.gallery__viewer',
        track: '.gallery__track',
        slide: '.gallery__slide',
        media: '.gallery__media',
        thumbnails: '.gallery__thumbs',
        thumbItem: '.gallery__thumb',
        thumbBtn: '.gallery__thumb-btn',
        nav: '.gallery__nav',
        navPrev: '.gallery__nav-btn--prev',
        navNext: '.gallery__nav-btn--next',
        dots: '.gallery__dots',
        dot: '.gallery__dot',
        counter: '.gallery__counter',
        liveRegion: '.gallery__status'
      };
    }

    static get CLASSES() {
      return {
        active: 'is-active',
        visible: 'is-visible',
        transitioning: 'is-transitioning',
        loaded: 'is-loaded',
        dynamicHeight: 'gallery--dynamic-height',
        slideMode: 'gallery--slide-mode',
        prevSlide: 'is-prev'
      };
    }

    constructor() {
      super();
      this.state = {
        currentIndex: 0,
        isTransitioning: false,
        isMobile: false
      };
      
      this.elements = {};
      this.slides = [];
      this.mediaData = [];
      this.observers = {};
      this.boundHandlers = {};
      this.desktopLayout = 'stacked';
    }

    connectedCallback() {
      this.cacheElements();
      if (!this.elements.track || this.slides.length === 0) return;
      
      this.desktopLayout = this.dataset.desktopLayout || 'stacked';
      this.cacheMediaData();
      this.setupMediaQueries();
      this.bindEvents();
      this.setupIntersectionObserver();
      this.setupResizeObserver();
      this.initializeGallery();
      const productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      if (productInfo && Shopify.postLinksRetry) productInfo.initShareLinks();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    cacheElements() {
      const S = MediaGallery.SELECTORS;
      
      this.elements = {
        viewer: this.querySelector(S.viewer),
        track: this.querySelector(S.track),
        thumbnails: this.querySelector(S.thumbnails),
        nav: this.querySelector(S.nav),
        navPrev: this.querySelector(S.navPrev),
        navNext: this.querySelector(S.navNext),
        dots: this.querySelector(S.dots),
        counter: this.querySelector(S.counter),
        liveRegion: this.querySelector(S.liveRegion)
      };
      
      this.slides = Array.from(this.querySelectorAll(S.slide));
      this.thumbItems = this.elements.thumbnails 
        ? Array.from(this.elements.thumbnails.querySelectorAll(S.thumbItem))
        : [];
      this.dotItems = this.elements.dots 
        ? Array.from(this.elements.dots.querySelectorAll(S.dot))
        : [];
    }

    cacheMediaData() {
      this.mediaData = this.slides.map((slide, index) => {
        const media = slide.querySelector(MediaGallery.SELECTORS.media);
        const img = media?.querySelector('img');
        
        return {
          index,
          id: slide.dataset.mediaId,
          type: slide.dataset.mediaType || 'image',
          aspectRatio: parseFloat(slide.dataset.aspectRatio) || 1,
          width: img?.naturalWidth || 0,
          height: img?.naturalHeight || 0,
          loaded: img?.complete && img?.naturalHeight > 0
        };
      });
    }

    setupMediaQueries() {
      this.mql = window.matchMedia('(min-width: 750px)');
      this.state.isMobile = !this.mql.matches;
      
      this.boundHandlers.onMediaQueryChange = this.handleMediaQueryChange.bind(this);
      this.mql.addEventListener('change', this.boundHandlers.onMediaQueryChange);
    }

    handleMediaQueryChange(e) {
      this.state.isMobile = !e.matches;
      
      if (this.state.isMobile) {
        this.disableSlideMode();
        this.slides.forEach(s => {
          s.style.transform = '';
        });
        if (this.classList.contains(MediaGallery.CLASSES.dynamicHeight)) {
          this.updateHeight();
        }
        this.scrollToSlide(this.state.currentIndex, false);
      } else {
        if (this.elements.viewer) {
          this.elements.viewer.style.height = '';
        }
        if (this.desktopLayout === 'thumbnail' || this.desktopLayout === 'thumbnail_slider') {
          this.enableSlideMode();
          this.scrollToSlide(this.state.currentIndex, false);
        }
      }
    }

    bindEvents() {
      if (this.elements.navPrev) {
        this.boundHandlers.onPrevClick = () => this.goTo(this.state.currentIndex - 1);
        this.elements.navPrev.addEventListener('click', this.boundHandlers.onPrevClick);
      }
      
      if (this.elements.navNext) {
        this.boundHandlers.onNextClick = () => this.goTo(this.state.currentIndex + 1);
        this.elements.navNext.addEventListener('click', this.boundHandlers.onNextClick);
      }
      
      this.thumbItems.forEach((thumb, index) => {
        const btn = thumb.querySelector(MediaGallery.SELECTORS.thumbBtn);
        if (btn) {
          btn.addEventListener('click', () => this.goTo(index));
        }
      });
      
      this.dotItems.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });
      
      this.slides.forEach((slide, index) => {
        const img = slide.querySelector('img');
        if (img && !img.complete) {
          img.addEventListener('load', () => {
            this.mediaData[index].loaded = true;
            this.mediaData[index].width = img.naturalWidth;
            this.mediaData[index].height = img.naturalHeight;
            this.mediaData[index].aspectRatio = img.naturalWidth / img.naturalHeight;
            
            if (index === this.state.currentIndex && this.state.isMobile) {
              this.updateHeight();
            }
          }, { once: true });
        }
      });
      
      this.boundHandlers.onKeyDown = this.handleKeyDown.bind(this);
      this.addEventListener('keydown', this.boundHandlers.onKeyDown);
      
      if (this.elements.track) {
        let scrollTimeout;
        let isScrolling = false;
        
        this.boundHandlers.onScroll = () => {
          if (!this.state.isMobile) return;
          isScrolling = true;
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => {
            isScrolling = false;
            this.onScrollEnd();
          }, 100);
        };
        this.elements.track.addEventListener('scroll', this.boundHandlers.onScroll, { passive: true });
      }
    }

    onScrollEnd() {
      if (!this.state.isMobile || !this.elements.track) return;
      
      const track = this.elements.track;
      const scrollLeft = track.scrollLeft;
      const slideWidth = this.slides[0]?.offsetWidth || track.offsetWidth;
      const nearestIndex = Math.round(scrollLeft / slideWidth);
      const clampedIndex = Math.max(0, Math.min(nearestIndex, this.slides.length - 1));
      
      if (clampedIndex !== this.state.currentIndex) {
        this.setActiveSlide(clampedIndex, true);
      } else {
        this.updateHeight();
      }
    }

    snapToNearestSlide() {}

    setupIntersectionObserver() {
      if (!this.elements.track) return;
      
      this.observers.intersection = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
              const index = this.slides.indexOf(entry.target);
              if (index !== -1 && index !== this.state.currentIndex) {
                this.setActiveSlide(index, !this.state.isMobile);
              }
            }
          });
        },
        {
          root: this.elements.track,
          threshold: [0.5, 1],
          rootMargin: '0px'
        }
      );
      
      this.slides.forEach(slide => {
        this.observers.intersection.observe(slide);
      });
    }

    setupResizeObserver() {
      this.observers.resize = new ResizeObserver(
        debounce(() => {
          if (this.state.isMobile) {
            this.updateHeight();
          }
        }, 100)
      );
      
      if (this.elements.viewer) {
        this.observers.resize.observe(this.elements.viewer);
      }
    }

    initializeGallery() {
      const activeSlide = this.slides.find(s => s.classList.contains(MediaGallery.CLASSES.active));
      this.state.currentIndex = activeSlide ? this.slides.indexOf(activeSlide) : 0;
      this.setActiveSlide(this.state.currentIndex, false);
      if (this.state.isMobile) {
        if (this.classList.contains(MediaGallery.CLASSES.dynamicHeight)) {
          this.initializeMobileHeight();
        }
      } else {
        if (this.desktopLayout === 'thumbnail' || this.desktopLayout === 'thumbnail_slider') {
          this.enableSlideMode();
          this.scrollToSlide(this.state.currentIndex, false);
        }
      }
      this.classList.add(MediaGallery.CLASSES.loaded);
    }

    initializeMobileHeight() {
      const slide = this.slides[this.state.currentIndex];
      const img = slide?.querySelector('img');
      if (img) {
        if (img.complete && img.naturalHeight > 0) {
          this.updateHeight();
        } else {
          img.addEventListener('load', () => {
            this.updateHeight();
          }, { once: true });
          setTimeout(() => {
            this.updateHeight();
          }, 100);
        }
      } else {
        this.updateHeight();
      }
    }

    goTo(index, animate = true) {
      if (this.state.isTransitioning) return;
      const targetIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      if (targetIndex === this.state.currentIndex) return;
      this.setActiveSlide(targetIndex, animate);
      this.scrollToSlide(targetIndex, animate);
    }

    goToMediaId(mediaId, prepend = false) {
      const index = this.slides.findIndex(s => s.dataset.mediaId === mediaId);
      if (index === -1) return;
      if (prepend) {
        this.prependSlide(index);
      } else {
        this.goTo(index);
      }
    }

    prependSlide(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.elements.track.prepend(slide);
      this.slides.splice(index, 1);
      this.slides.unshift(slide);
      if (this.thumbItems[index]) {
        const thumb = this.thumbItems[index];
        this.elements.thumbnails?.querySelector('ul')?.prepend(thumb.parentElement || thumb);
        this.thumbItems.splice(index, 1);
        this.thumbItems.unshift(thumb);
      }
      const mediaItem = this.mediaData.splice(index, 1)[0];
      this.mediaData.unshift(mediaItem);
      this.setActiveSlide(0, false);
    }

    scrollToSlide(index, animate = true) {
      const slide = this.slides[index];
      if (!slide || !this.elements.track) return;
      if (!this.state.isMobile && this.classList.contains(MediaGallery.CLASSES.slideMode)) {
        const translateX = -(index * 100);
        this.slides.forEach(s => {
          s.style.transform = `translateX(${translateX}%)`;
        });
        return;
      }
      const scrollLeft = slide.offsetLeft;
      this.elements.track.scrollTo({
        left: scrollLeft,
        behavior: animate ? 'smooth' : 'instant'
      });
    }

    setActiveSlide(index, updateHeight = true) {
      const prevIndex = this.state.currentIndex;
      this.state.currentIndex = index;
      this.slides.forEach((slide, i) => {
        const isActive = i === index;
        const isPrev = i < index;
        slide.classList.toggle(MediaGallery.CLASSES.active, isActive);
        slide.classList.toggle(MediaGallery.CLASSES.prevSlide, isPrev);
      });
      this.updateThumbnails(index);
      this.updateDots(index);
      this.updateCounter(index);
      this.updateNavButtons(index);
      if (updateHeight && this.state.isMobile) {
        this.updateHeight();
      }
      this.handleMediaPlayback(index, prevIndex);
      this.announceSlide(index);
      this.dispatchEvent(new CustomEvent('slideChanged', {
        detail: {
          currentIndex: index,
          previousIndex: prevIndex,
          currentElement: this.slides[index],
          mediaId: this.slides[index]?.dataset.mediaId
        }
      }));
    }

    updateThumbnails(index) {
      if (!this.elements.thumbnails) return;
      
      this.thumbItems.forEach((thumb, i) => {
        const btn = thumb.querySelector(MediaGallery.SELECTORS.thumbBtn);
        thumb.classList.toggle(MediaGallery.CLASSES.active, i === index);
        btn?.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      const activeThumb = this.thumbItems[index];
      if (activeThumb) {
        const thumbList = this.elements.thumbnails.querySelector('ul');
        if (thumbList) {
          const thumbRect = activeThumb.getBoundingClientRect();
          const listRect = thumbList.getBoundingClientRect();
          
          if (thumbRect.left < listRect.left || thumbRect.right > listRect.right) {
            thumbList.scrollTo({
              left: activeThumb.offsetLeft - (listRect.width / 2) + (thumbRect.width / 2),
              behavior: 'smooth'
            });
          }
        }
      }
    }

    updateDots(index) {
      this.dotItems.forEach((dot, i) => {
        dot.classList.toggle(MediaGallery.CLASSES.active, i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    updateCounter(index) {
      if (!this.elements.counter) return;
      
      const current = this.elements.counter.querySelector('.gallery__counter-current');
      if (current) {
        current.textContent = index + 1;
      }
    }

    updateNavButtons(index) {
      if (this.elements.navPrev) {
        this.elements.navPrev.disabled = index === 0;
      }
      if (this.elements.navNext) {
        this.elements.navNext.disabled = index === this.slides.length - 1;
      }
    }

    enableDynamicHeight() {
      this.classList.add(MediaGallery.CLASSES.dynamicHeight);
    }

    disableDynamicHeight() {
      this.classList.remove(MediaGallery.CLASSES.dynamicHeight);
      if (this.elements.viewer) {
        this.elements.viewer.style.height = '';
      }
    }

    enableSlideMode() {
      this.classList.add(MediaGallery.CLASSES.slideMode);
    }

    disableSlideMode() {
      this.classList.remove(MediaGallery.CLASSES.slideMode);
    }

    updateHeight() {
      if (!this.classList.contains(MediaGallery.CLASSES.dynamicHeight)) return;
      if (!this.state.isMobile || !this.elements.viewer) return;
      
      const slide = this.slides[this.state.currentIndex];
      if (!slide) return;
      
      const img = slide.querySelector('img');
      if (!img) return;
      
      if (!img.complete || img.naturalWidth === 0) {
        img.addEventListener('load', () => this.updateHeight(), { once: true });
        return;
      }
      
      const slideWidth = slide.offsetWidth;
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      const imageHeight = slideWidth / aspectRatio;
      
      this.elements.viewer.style.height = `${Math.round(imageHeight)}px`;
    }

    handleKeyDown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.goTo(this.state.currentIndex - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.goTo(this.state.currentIndex + 1);
      }
    }

    handleMediaPlayback(currentIndex, prevIndex) {
      if (window.pauseAllMedia) {
        window.pauseAllMedia();
      }
      
      const currentSlide = this.slides[currentIndex];
      const deferredMedia = currentSlide?.querySelector('.deferred-media');
      if (deferredMedia && deferredMedia.loadContent) {
        deferredMedia.loadContent(false);
      }
    }

    announceSlide(index) {
      if (!this.elements.liveRegion) return;
      
      const slide = this.slides[index];
      const img = slide?.querySelector('img');
      
      if (img) {
        this.elements.liveRegion.setAttribute('aria-hidden', 'false');
        this.elements.liveRegion.textContent = window.accessibilityStrings?.imageAvailable?.replace('[index]', index + 1) 
          || `Image ${index + 1} of ${this.slides.length}`;
        
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', 'true');
        }, 2000);
      }
    }

    setActiveMedia(mediaId, prepend = false) {
      this.goToMediaId(mediaId, prepend);
    }

    preventStickyHeader() {
      const stickyHeader = document.querySelector('sticky-header');
      if (stickyHeader) {
        stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }
    }

    cleanup() {
      if (this.boundHandlers.onMediaQueryChange) {
        this.mql?.removeEventListener('change', this.boundHandlers.onMediaQueryChange);
      }
      
      if (this.elements.track) {
        this.elements.track.removeEventListener('scroll', this.boundHandlers.onScroll);
      }
      
      this.removeEventListener('keydown', this.boundHandlers.onKeyDown);
      
      Object.values(this.observers).forEach(observer => {
        observer?.disconnect();
      });
    }
  });
}